import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components'
import {
  QUERIES_ADMIN,
  useOrderManagmentPurchaseOrderItems,
  useSupplierOrderEdit,
} from '@/services'

import { ProductQualityCategoryEnum, SupplyOrderProps } from '@/types'
import { showToast } from '@mercai/clever'
import { useQueryClient } from '@tanstack/react-query'

import { SupplyOrderItemsWithLinks } from './components'

import { TabProductsProps } from './tab-products.type'

import styles from './tab-products.module.css'

const TabProductsLoading = () => {
  return (
    <div className={styles.container}>
      <div>
        <SupplyOrderItemsWithLinks isLoading />
      </div>
    </div>
  )
}

export const TabProducts = ({ supplyOrder, isLoading }: TabProductsProps) => {
  const { t } = useTranslation('screenSupplyOrderDetail')

  const queryClient = useQueryClient()

  const { data, isLoading: isLoadingLinks } =
    useOrderManagmentPurchaseOrderItems(supplyOrder?.uuid || '')

  const { mutateAsync } = useSupplierOrderEdit(supplyOrder?.uuid)

  const onChangeCategory = async (
    supplyOrderItemUuid: string,
    category?: keyof typeof ProductQualityCategoryEnum,
  ) => {
    const payload: Partial<SupplyOrderProps> = {
      uuid: supplyOrder?.uuid,
      items: supplyOrder?.items?.map((item) => {
        if (item.uuid === supplyOrderItemUuid) {
          return { uuid: item.uuid, category }
        }
        return { uuid: item.uuid }
      }),
    }

    const response = await mutateAsync(payload)

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module, supplyOrder?.uuid],
      })
      queryClient.invalidateQueries({
        queryKey: [
          QUERIES_ADMIN.BFF_SUPPLIER_ORDER_MANAGMENT_PURCHASE_ORDER_ITEMS
            .module,
          supplyOrder?.uuid,
        ],
      })
    } else {
      showToast({
        variant: 'error',
        title: response?.message,
      })
    }
  }

  if (isLoading || isLoadingLinks) {
    return <TabProductsLoading />
  }

  if (!data?.data || data?.data.length === 0) {
    return (
      <div className={styles.container}>
        <EmptyState
          horizontal
          description={t('tabProductsEmptyTitle')}
          title={t('tabProductsEmptyDescription')}
        />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {data?.data?.map((item) => (
        <div key={item.uuid}>
          <SupplyOrderItemsWithLinks
            item={item}
            onChangeCategory={onChangeCategory}
          />
        </div>
      ))}
    </div>
  )
}
